/**
 * CareStreakBadge (#721) — the health signal, in one line.
 *
 * Kylie (HTHD receptionist, ex-Dogtopia): "it would keep track of how many
 * nights they didn't [eat]." This is that count, rendered wherever a dog
 * appears on a staff surface — the kennel roster, the pet ID card the front
 * desk opens at check-in, and the dog's own record.
 *
 * DESIGN-PHILOSOPHY:
 *  · §5 never lie about data — the badge renders ONLY on a real refusal run.
 *    "No streak" and "we could not read the log" both render NOTHING rather
 *    than a reassuring "eating well" the data does not support. A missing
 *    badge is never a claim.
 *  · §Color roles — the `--ferro-warning-*` family at 1–2 days, the
 *    `--ferro-error-*` family at 3+, and color is never the only signal: the
 *    count is in the text and in the aria-label, so it reads identically to
 *    a screen reader and in monochrome.
 *  · §7 hands not cursors — no hover-only affordance; `title` is a bonus for
 *    pointer users, and everything it says is already visible.
 *  · §Anti-patterns — no raw hex, no ad-hoc px; every value is a token.
 */

import './care-log.css';

export interface CareStreakBadgeProps {
  /** Ready-made server copy, e.g. "Ate none at dinner 2 days running". */
  label: string | null;
  /** Length of the run in consecutive days. 0/absent ⇒ nothing renders. */
  days: number;
  /** `sm` for dense rows (roster, arrivals), `md` for a record page. */
  size?: 'sm' | 'md';
  /** Extra context for assistive tech, e.g. the dog's name. */
  dogName?: string;
  testId?: string;
}

/** 3+ consecutive refusals is the point a vet conversation starts. */
const SEVERE_AFTER_DAYS = 3;

export function CareStreakBadge({
  label,
  days,
  size = 'sm',
  dogName,
  testId = 'care-streak-badge',
}: CareStreakBadgeProps): JSX.Element | null {
  // Honest silence. Not an "all good" chip — we did not verify that.
  if (!label || days < 1) return null;

  const level = days >= SEVERE_AFTER_DAYS ? 'severe' : 'watch';
  const ariaLabel = dogName ? `${dogName}: ${label}` : label;

  return (
    <span
      className={`ferro-care-streak ferro-care-streak--${level} ferro-care-streak--${size}`}
      role="status"
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={testId}
      data-streak-level={level}
      data-streak-days={days}
    >
      {/* Empty bowl — decorative; the text carries the meaning. */}
      <svg
        className="ferro-care-streak__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M3 11h18" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <path d="M8 18h8" />
      </svg>
      <span className="ferro-care-streak__text">{label}</span>
    </span>
  );
}
